
import * as $ from 'jquery';

import { ILink } from 'projects/axefeedback';
import { GeneratedAlternativeText } from 'projects/common/guidance/src/components/alternative-text/AlternativeTextStorage';
import { TableHeaderOrientation } from 'projects/common/guidance/src/components/fix-table-headers/fix-table-headers.component';

import { isDecorative } from 'src/shared/preprocessor';

import { findNodeByXpath, replaceTagName } from '../DomUtil';
import { whenHasValue, hasValue, orElse, assertValue } from '../NullUtil';

/** Ultra file/image attachment metadata */
interface IUltraAttachment {
    'alternativeText': string;
    'displayName': string;
    'fileName': string;
    'isDecorative': boolean;
}

export interface IBaseFeedback {
    feedback: any;
    explanationData: any;
}

export function transformHtmlFragmentExplanationData(
    document: Document,
    rawExplanationData: IBaseFeedback['explanationData'],
    html: string,
    preprocessType: string
): IBaseFeedback['explanationData'] {
    // It's probably best to fix explanationData here because the way we have to convert from html fragment xpath to
    // internal wysiwyg xpath is going to be dependent on which editor is being used
    const fixedExplanationData = { 'type': rawExplanationData.type } as any;

    // For some rules, we need to scrape additional context out about the rule to help guide decisions on how they
    // could be automatically fixed. In some cases we switch from the simple "xpaths" model that just has an array
    // of xpath strings to a "violations" model that contains the xpath string with more metadata context about the
    // issue. This means that other areas that handle this data (such as instructor feedback), don't have to parse
    // and analyze the HTML to come up with similar conclusions about how to fix
    Object.keys(rawExplanationData)
        .map((ruleName) => [ruleName, rawExplanationData[ruleName]])
        .filter(([, rawRuleExplanationData]) => typeof rawRuleExplanationData === 'object')
        .forEach(([ruleName, rawRuleExplanationData]) => {
            const fixedRuleExplanationData: Record<string, any> & { xpaths?: string[] } =
                fixedExplanationData[ruleName] = { ...rawRuleExplanationData };

            // Apply rule-specific transforms
            const { xpaths }: { xpaths?: string[] } = fixedRuleExplanationData;
            if (hasValue(xpaths) && ruleName === 'HtmlHeadingOrder') {
                // We're going to replace xpaths with violations that have more metadata about the issue
                delete fixedRuleExplanationData.xpaths;

                const $content = $('<div></div>').html(html);
                const headingsWithIndex = $content.find('h1,h2,h3,h4,h5,h6').toArray().map((el, i) => ({
                    el,
                    i,
                    'level': parseInt(el.tagName.slice(1), 10)
                }));

                fixedRuleExplanationData.headings = headingsWithIndex.map((hwi) => ({ 'level': hwi.level }));
                fixedRuleExplanationData.violations = xpaths.map((xpath) => {
                    const headingEl = findNodeByXpath(document, $content[0], xpath);
                    const heading = headingsWithIndex.find((hwi) => hwi.el === headingEl);
                    const headingIndex = orElse<{i: number}>(heading, { 'i': -1 }).i;
                    return { headingIndex, xpath };
                });
            } else if (
                hasValue(xpaths) &&
                    ['HtmlImageAlt', 'HtmlImageRedundantAlt'].indexOf(ruleName) !== -1
            ) {
                // We're going to replace xpaths with violations that have more metadata about the issue
                delete fixedRuleExplanationData.xpaths;

                const $content = $('<div></div>').html(html);
                fixedRuleExplanationData.violations = xpaths.map((xpath: string) => {
                    const img = findNodeByXpath(document, $content[0], xpath);
                    const $img = hasValue(img) ? $(img) : null;
                    const decorative = isDecorative(img, preprocessType);
                    const description = decorative
                        ? null
                        : hasValue($img)
                            ? orElse($img.attr('alt'), null)
                            : null;

                    const validatedSource = async () : Promise<string | undefined> => {
                        const imageSrc = $img?.attr('src');
                        if (hasValue(imageSrc)) {
                            // only perform head request if we are in learn
                            // eslint-disable-next-line max-len
                            const finalUrl = preprocessType === 'learn' ? await performHeadRequestOnLearnImage(imageSrc) : imageSrc;
                            return `src="${finalUrl}"`;
                        } else {
                            return undefined;
                        }
                    };
                    const metadata = { decorative, description, 'src': validatedSource };
                    return { metadata, xpath };
                });
            } else if (hasValue(xpaths) && ruleName === 'HtmlObjectAlt') {
                // TODO: This transform is actually TinyMCE-specific. So would need to be split out into the editor
                // controller

                // TinyMCE transforms a raw <object> tag into an <img>, so in order to properly show preview
                // highlights we have to convert the xpath from the raw html to the wysiwyg representation
                fixedRuleExplanationData.xpaths = xpaths.map((xpath) => {
                    const split = xpath.split('/');
                    const prefix = split.slice(0, -1).join('/');
                    const last = split.pop();
                    if (hasValue(last) && last.startsWith('object')) {
                        return `${prefix}/img[contains(@class, 'mce-object-object')]`;
                    } else if (hasValue(last) && last.startsWith('*[@id=')) {
                        return `${prefix}/${last.replace('@id=', '@data-mce-p-id=')}`;
                    } else {
                        return `${prefix}/${last}`;
                    }
                });
            } else {
                fixedRuleExplanationData.xpaths = xpaths;
            }
        });
    return fixedExplanationData;
}

export const styleAttributes = [
    'font-size', 'font-weight', 'font-family', 'font-style', 'text-decoration', 'text-transform'
];

/**
 * Parse CSS styles relevant for block styles from element
 *
 * @param element Element whose styles will be used
 */
export function parseBlockStyles(element: JQLite) {
    const style: { [_: string]: string } = {};

    styleAttributes.forEach((attr: any) => {
        const value = element.css(attr);
        if (hasValue(value)) {
            style[attr] = value;
        }
    });

    return style;
}

/** Given an HTML tag, parse out the heading level. If the tag is not a heading, it will return NaN */
export function parseBlockLevel(tag: string): number {
    return Number.parseInt($.trim(tag).substring(1), 10);
}

/**
 * The default selection for visual highlighting of an element can result in a html element that is not valid to be
 * shown on its own (such as a single <th></th> element). When this happens, move up the tree until we get to a valid
 * selection.
 * @param element the element that is being highlighted in the IF.
 */
export function getDisplayableElement(element: HTMLElement | undefined): string | undefined {
    let elementToReturn = element;
    const excludedTags = ['th', 'tr', 'tbody', 'iframe'];
    while (hasValue(elementToReturn) &&
            excludedTags.includes(elementToReturn?.tagName.toLowerCase()) &&
            hasValue(elementToReturn.parentElement)) {
        elementToReturn = elementToReturn.parentElement;
    }
    return elementToReturn?.outerHTML;
}

/**
 * When an image isn't saved in Learn yet, it doesn't have the xid- tad, and we need to make a HEAD request to
 * get the final URL of the image. This method does that and returns the displayable element in string format.
 * @param issue The issue element
 * @private
 */
export async function processLearnImageAndGetDisplayableElement(
    issue: HTMLElement | undefined
): Promise<string | undefined> {
        if (hasValue(issue)) {
            const url = issue.getAttribute('src');
            const newAttribute = await performHeadRequestOnLearnImage(url!);
            if (hasValue(newAttribute)) {
                issue.setAttribute('src', newAttribute);
            }
            let displayableElement = getDisplayableElement(issue);
            displayableElement = decodeHtmlEntitiesInUrl(displayableElement!);
            return displayableElement;
        }
    return getDisplayableElement(issue);
}

async function performHeadRequestOnLearnImage(url: string): Promise<string | undefined> {
    try {
        if (hasValue(url) && isValidURL(url)) {
            const lastPartOfUrl = url.substring(url.lastIndexOf('/') + 1);
            if (!lastPartOfUrl.includes('xid-')) {
                const response = await fetch(url, {
                    'method': 'HEAD'
                });
                return response.url;
            } else {
                return url;
            }
        }
    } catch (e) {
        return url;
    }
}

/**
 * After converting to string all & are converted to &amp; in the URL. This method decodes them back to &.
 * @param url The URL to decode
 * @private
 */
function decodeHtmlEntitiesInUrl(url:string) {
    return url.replace(/&amp;/g, '&');
}

/**
 * Checks if the URL is valid.
 * @param url The URL to check
 * @private
 */
function isValidURL(url: string): boolean {
    try {
        return Boolean(new URL(url).toString());
    } catch (_) {
        return false;
    }
}

/**
 * Given an element, lookup ultra attachment data and update it with the given metadata
 *
 * @param el Image element for which to update the ultra attachment data
 * @param metadata Metadata to update
 */
export function ultraAttachment(el: HTMLElement, metadata: Partial<IUltraAttachment>) {
    const attachment = $(el).parents('[data-bbtype="attachment"][data-bbfile]');
    whenHasValue(attachment.attr('data-bbfile'), (bbFile) =>
        attachment.attr('data-bbfile', JSON.stringify({
            ...JSON.parse(bbFile),
            ...metadata
        }))
    );
}

/**
 * Apply given fix function based on the rule name
 * @param ruleName Acceptable rule names
 * @param locators Array of XPaths, used to locate elements in the content
 * @param fixOptions Fix options associated with the rule
 * @param content Content to search in
 */
export function applyWysiwygFix(
    ruleName: string,
    locators: string[],
    fixOptions: FixOptions,
    content: HTMLElement
): boolean {
    const fixFunction = ruleFixes[ruleName];
    const elements = findElements(locators, content);
    if (hasValue(fixFunction) && elements.length > 0) {
        elements.forEach(fixFunction(fixOptions));
        return true;
    }
    return false;
}

/**
 * Finds all elements for given locators
 * @param locators Array of XPaths
 * @param content Content to search in
 */
const findElements = (
    locators: string[],
    content: HTMLElement
) => {
    return locators
        .map((xpath) => findNodeByXpath(document, content, xpath))
        .filter((el): el is HTMLElement => hasValue(el));
};
/**
 * Apply the color contrast fix to the given node
 * @param fixOptions The fix options associated with the rule
 */
const applyHtmlColorContrast = (
    fixOptions: FixOptions
) => (
    node: HTMLElement
) => {
    whenHasValue(fixOptions.color, (color) => {
        const $node = $(node);
        // Set the root element color
        $node.css('color', color);

        // Set the color of all explicitly-colored descendants
        $node.find('*').each((_, el) => {
            const $el = $(el);
            if ($el.css('color') !== '') {
                $el.css('color', color);
            }
        });
    });
};
/**
 * Apply the header fix to the given table
 * @param fixOptions The fix options associated with the rule
 */
const applyHtmlTdHasHeader = (
    fixOptions: FixOptions
) => (
    table: HTMLElement
) => {
    const $table = $(table);
    orElse(fixOptions.orientation, [])
        .map((o: TableHeaderOrientation) =>
            o === 'column' ? 'tr > td:first-child' : 'tr:first-child > td')
        .forEach((selector: string) => {
            $table.find(selector)
                .toArray()
                // Filter out nested table tr's
                .filter((el) => $(el).closest('table').is($table))
                .forEach((el) => replaceTagName($, el, 'th'));
        });
};
/**
 * Apply the image alt text fix to the given image
 * @param fixOptions The fix options associated with the rule
 */
const applyHtmlImageAlt = (
    fixOptions: FixOptions
) => (
    el: HTMLElement
) => {
    if ('save' in fixOptions) {
        $(el).attr('alt', assertValue(fixOptions.save).userInput);
        ultraAttachment(el, { 'alternativeText': assertValue(fixOptions.save).userInput });
    } else if (fixOptions.decorative) {
        $(el).attr('alt', '');
        // In addition to setting alt="", also set role="presentation". This has multiple benefits:
        // - It indicates an explicit choice of marking the image as decorative
        // - In Learn Original, we should mimic the backend and manipulate the fragment of the src
        //   attribute. This is fairly brittle though. By adding role="presentation", we can bypass that
        // - Moodle's Atto editor will pick up on it and renders the "Marked as decorative" checkbox
        $(el).attr('role', 'presentation');
        // For Blackboard ultra the isDecorative flag overrides other settings, so needs to be applied.
        ultraAttachment(el, { 'isDecorative': fixOptions.decorative });
    } else if (fixOptions.remove) {
        $(el).removeAttr('alt');
        $(el).removeAttr('role');
        ultraAttachment(el, { 'alternativeText': '', 'isDecorative': false });
    }
};

/**
 * Apply the link name fix to the given link
 * @param fixOptions The fix options associated with the rule
 */
const applyHtmlLinkName = (
    fixOptions: FixOptions
) => (
    el: HTMLElement
) => {
    if (hasValue(fixOptions.remove)) {
        replaceTagName($, el, 'span');
    } else {
        whenHasValue(fixOptions.text, (text) => {
            const toAppend = $('<span></span>').text(text);
            $(el).append(toAppend);
        });
    }
};

/**
 * Apply the fix for empty headings to the given heading
 * Removes or replaces the heading based on the fix options
 * @param fixOptions The fix options associated with the rule
 */
const applyHtmlEmptyHeading = (
    fixOptions: FixOptions
) => (
    el: HTMLElement
) => {
    if (hasValue(fixOptions.remove)) {
        $(el).remove();
    } else if (hasValue(fixOptions.replace)) {
        $(el).replaceWith(document.createElement(fixOptions.replace));
    }
};

/**
 * Apply the fix for heading order to the given heading
 * Replaces the heading tag with the tag specified in the fix options
 * @param fixOptions The fix options associated with the rule
 */
const applyHtmlHeadingOrder = (
    fixOptions: FixOptions
) => (
    el: HTMLElement
) => {
    whenHasValue(fixOptions.tag, (tag) => {
        replaceTagName($, el, tag);
    });
};

/**
 * Apply the fix for broken links to the given link
 * Removes or replaces the link based on the fix options
 * @param fixOptions The fix options associated with the rule
 */
const applyHtmlBrokenLink = (
    fixOptions: FixOptions
) => (
    el: HTMLElement
) => {
    if (hasValue(fixOptions.remove)) {
        const element: JQLite = $(el);
        const imageChild: HTMLImageElement | undefined = element.children('img').get(0);
        if (typeof imageChild !== 'undefined') {
            element.replaceWith(imageChild);
        } else if (element.text() !== '') {
            const span: HTMLSpanElement = document.createElement('span');
            span.innerText = element.text();
            element.replaceWith(span);
        } else {
            element.remove();
        }
    } else if (hasValue(fixOptions.link)) {
        $(el).attr('href', fixOptions.link.url);
    }
};

/**
 * Utility method for element tree traversal building result in the passed in contextParts. Starting node is expected to
 * be an element node. Some inner nodes are ignored as they don't contain relevant text.
 *
 * @param node starting / current node
 * @param target target node
 * @param contextParts outptu array
 * @param tagName internal
 * @returns Index where the looked up target element would have been placed in the result array. Returns -1 if it's
*           not found (in the explored branch)
 */
const traverseExtractingText = (
    node: Node,
    target: Node,
    contextParts: {
        text: string,
        headerLevel?: number;
    }[],
    tagName?: string
): number|undefined => {
    if (node.nodeType === Node.TEXT_NODE) {
        let text = node.textContent?.trim();
        if (text) {
            const headerMatch = tagName ? tagName.match(/h([1-6])/i) : undefined;
            const headerLevel = headerMatch ? parseInt(headerMatch[1], 10) : undefined;
            if (headerLevel !== undefined) {
                text = `${'#'.repeat(headerLevel)} ${text}`;
            }

            contextParts.push({
                text,
                headerLevel
            });
        }
        return;
    } else if (node === target) {
        return contextParts.length;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
        let index: number|undefined = undefined;
        const element = node as HTMLElement;
        const tagName = element.tagName.toLowerCase();
        if (tagName == 'script' || tagName == 'style') {
            return;
        }
        if (element.classList.contains('ally-preview-selection-container')) {
            return;
        }
        for (let i = 0; i < node.childNodes.length; i++) {
            const innerIndex = traverseExtractingText(node.childNodes[i], target, contextParts, tagName);
            if (innerIndex !== undefined) index = innerIndex;
        }
        return index;
    }
};

/**
 * Extracts the text context surrounding the target element given by locator xpath inside the root element.
 * The context is extracted by traversing siblings in both back and forward from the target. In case headers are
 * included they are extracted first.
 *
 * @param root JQuery container to extract text from
 * @param locator XPath locator to find the element
 * @param includeAllHeaders Whether to include all previous headers in the context
 * @param maxLength Maximum length of the context to extract
 */
export function extractContextAroundElement(
    root: Element,
    locator: string,
    includeAllHeaders: boolean,
    maxLength: number,
    targetText: string
): string|undefined {
    const targetElement = findNodeByXpath(document, root, locator);
    if (!hasValue(targetElement)) {
        return undefined;
    }

    const contextParts: {
        text: string,
        headerLevel?: number;
        included?: true
    }[] = [];
    const targetIndex = traverseExtractingText(
        root,
        targetElement,
        contextParts
    );
    if (targetIndex === undefined) return undefined;

    let currentLength = 0;
    if (includeAllHeaders) {
        for (let i=0; i<contextParts.length; i++) {
            const part = contextParts[i];
            if (i >= targetIndex) break;
            if (part.headerLevel !== undefined) {
                part.included = true;
                currentLength += part.text.length;
            }
        }
    }

    // include target
    contextParts.splice(targetIndex, 0, {
        'text': targetText,
        'included': true
    });
    let forwardIndex = targetIndex+1;
    let backIndex = targetIndex-1;

    while (
        currentLength < maxLength &&
        !(backIndex < 0 && forwardIndex >= contextParts.length)
    ) {
        // add another text backwards
        if (backIndex >= 0) {
            const part = contextParts[backIndex];
            if (!part.included) {
                part.included = true;
                currentLength += part.text.length;
            }
            backIndex--;
        }
        if (currentLength < maxLength) {
            // add another text forward
            if (forwardIndex < contextParts.length) {
                const part = contextParts[forwardIndex];
                if (!part.included) {
                    part.included = true;
                    currentLength += part.text.length;
                }
                forwardIndex++;
            }
        }
    }

    const context = contextParts
        .filter((part) => part.included)
        .map((part) => {
            if (part.headerLevel !== undefined || part.text == targetText) {
                return `\n\n${part.text}\n`;
            } else {
                return `${part.text} `;
            }
        })
        .join('').trim();

    // make sure we never exceed the max length
    return context.substring(0, maxLength).trim();
}

export function extractContextAroundImage(root: Element, locator: string): string|undefined {
    return extractContextAroundElement(
        root, locator,
        true, // include all prior headers
        5000, // extract up to 5000 characters of text before and after the image (it total)
        '<[ described image ]>' // representation at the place inside context where image would have been
    );
}

export type FixOptions = {
    color?: string;
    orientation?: TableHeaderOrientation[];
    save?: GeneratedAlternativeText;
    decorative?: boolean;
    remove?: boolean;
    text?: string;
    replace?: string;
    tag?: string;
    link?: ILink;
}

type RuleFunction = (fixOptions: FixOptions) => (el: HTMLElement) => void;

// Define a map where each rule name is associated with its corresponding fix function
const ruleFixes: Record<string, RuleFunction> = {
    'HtmlColorContrast': applyHtmlColorContrast,
    'HtmlTdHasHeader': applyHtmlTdHasHeader,
    'HtmlEmptyHeading': applyHtmlEmptyHeading,
    'HtmlHeadingOrder': applyHtmlHeadingOrder,
    'HtmlImageAlt': applyHtmlImageAlt,
    'HtmlImageRedundantAlt': applyHtmlImageAlt,
    'HtmlLinkName': applyHtmlLinkName,
    'HtmlBrokenLink': applyHtmlBrokenLink
};
